/**
 * RELATÓRIO DE PEDIDOS — a linha de venda real da Lucco.
 *
 * O QUE ESTAVA AQUI ANTES: `<DynamicTable tableType={4} />`. O tableType 4 é a
 * tabela de FORNECEDORES — a tela chamada "Relatório de Pedidos" mostrava a
 * lista de fornecedores do cadastro. Não era um relatório incompleto; era
 * fiação errada. É o que este arquivo desfaz.
 *
 * Agora vem de `/reports/ordens`, que lê `aod."AotOrder"` + `aod."AotOrderInfo"`
 * por SQL cru — a venda NÃO está no Central (`acd."ActOrder"` tem 0 linhas,
 * contado no dump restaurado). Mesmo `DynamicTable`, mesmos estilos, mesmo
 * filtro/ordenação/pin/export de sempre: mudou de onde vem o dado, não como ele
 * aparece.
 *
 * SÓ LEITURA (§5.1): sem "Criar", sem "Importar", sem modal de edição.
 */
import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import * as AlfredoStyles from "../styles/components/OrderReport";
import * as AlfredoComponents from "./";
import { relatorioService } from "../services/relatorio.service";
import { useRelatorio } from "../hooks/useRelatorio";
import { acharVisao } from "../comum/relatorios/visoes";
import { priorizarColunas } from "../comum/relatorios/colunas";

const CAMPOS = ["de", "ate", "turno", "canal", "visao", "produto", "categoria"];

const buscarOrdens = relatorioService.ordens.bind(relatorioService);

const OrderReport: React.FC = () => {
  // A visão é lida da URL ANTES da chamada, porque ela decide o `agruparPor` —
  // pedir o relatório e só depois descobrir o agrupamento faria duas chamadas.
  const [parametrosDaUrl] = useSearchParams();
  const visao = acharVisao(parametrosDaUrl.get("visao"));

  /**
   * A visão É filtro + agrupamento (§7.5). O `agruparPor` não vira campo de URL
   * próprio: é derivado da visão, para que não exista link incoerente (visão
   * "mix" com agrupamento de outra coisa).
   */
  const extras = useMemo(() => {
    if (!visao || visao.desabilitada) return { limite: 500 };
    return {
      limite: 500,
      agruparPor: visao.agruparPor.join(","),
      ...(visao.filtro ?? {}),
    };
  }, [visao]);

  const relatorio = useRelatorio(buscarOrdens, CAMPOS, extras);

  /**
   * As colunas que a visão destaca vêm na frente. `useMemo` obrigatório: o
   * DynamicTable reinicializa a tabela quando a referência de `columns` muda, e
   * um array novo a cada render zeraria largura, pin e colunas visíveis.
   */
  const colunas = useMemo(
    () => priorizarColunas(relatorio.colunas, visao?.colunas ?? []),
    [relatorio.colunas, visao]
  );

  const turnos = useMemo(() => {
    const nomes = new Set<string>();
    (relatorio.envelope?.linhas ?? []).forEach((linha: any) => {
      if (linha.turno) nomes.add(String(linha.turno));
    });
    return Array.from(nomes);
  }, [relatorio.envelope]);

  return (
    <AlfredoStyles.OrderReportContainer>
      <AlfredoStyles.Card>
        <AlfredoStyles.Icon>receipt_long</AlfredoStyles.Icon>
        <AlfredoStyles.Title>
          {visao ? visao.titulo : "Relatório de Pedidos"}
        </AlfredoStyles.Title>

        <AlfredoComponents.CarimboDoDado
          carimbo={relatorio.envelope?.carimbo}
          fonte={relatorio.envelope?.fonte}
        />

        <AlfredoComponents.FiltrosDoRelatorio
          de={relatorio.filtro.de}
          ate={relatorio.filtro.ate}
          turno={relatorio.filtro.turno}
          canal={relatorio.filtro.canal}
          visao={relatorio.filtro.visao}
          turnos={turnos}
          visaoEscolhida={visao}
          aoMudar={relatorio.mudarFiltro}
          aoLimpar={relatorio.limparFiltro}
        />

        <AlfredoComponents.AvisosDoRelatorio
          avisos={relatorio.envelope?.avisos}
          avisoDaVisao={visao?.desabilitada ?? visao?.avisoConhecido}
          comecarAberto={Boolean(visao?.avisoConhecido || visao?.desabilitada)}
        />

        <AlfredoComponents.DynamicTable
          tableType={0}
          handleOpenModal={() => undefined}
          rows={relatorio.linhas}
          columns={colunas}
          // Sem isto o id da linha cairia no fallback dos 7 UUIDs de cadastro,
          // ficaria `undefined`, e um clique marcaria todas as linhas juntas.
          getRowId={(linha) => String(linha.chave)}
          title={visao ? visao.titulo : "Linhas de pedido"}
          hideCreateButton
          hideImportButton
          hideRowActions
          isLoading={relatorio.carregando}
          error={relatorio.erro}
          emptyMessage="Nenhuma venda no recorte escolhido. Ajuste o período ou limpe os filtros."
        />

        {relatorio.envelope && (
          <AlfredoComponents.RodapeDaFonte envelope={relatorio.envelope} />
        )}
      </AlfredoStyles.Card>
    </AlfredoStyles.OrderReportContainer>
  );
};

export default OrderReport;
